import { createHooks } from 'hookable';
/** Runs handlers in order; mutations apply and a synchronous throw propagates. */
const syncCaller = (fns, args) => {
    for (const fn of fns) {
        const result = fn(...args);
        if (result instanceof Promise)
            result.catch(() => { });
    }
};
/** Runs handlers in order, swallowing errors (best-effort notification). A
 * handler that returns a promise is not awaited (this stays synchronous),
 * but its rejection is still caught so it can't become an unhandled rejection. */
const safeSyncCaller = (fns, args) => {
    for (const fn of fns) {
        try {
            const result = fn(...args);
            if (result instanceof Promise)
                result.catch(() => { });
        }
        catch {
            // best-effort: a failing notification listener must not break the op
        }
    }
};
/** Awaits handlers in order, swallowing errors (best-effort async notification). */
const safeAsyncCaller = async (fns, args) => {
    for (const fn of fns) {
        try {
            await fn(...args);
        }
        catch {
            // best-effort
        }
    }
};
/**
 * Typed event bus backed by unjs `hookable`. Exposes synchronous emit/notify
 * (for the synchronous Loro-backed managers) and async variants (for the
 * already-async FileStore upload path, where `before:` handlers may await).
 *
 * The underlying `Hookable` is exposed as `hooks` for advanced use — `addHooks`,
 * `beforeEach`/`afterEach` spies, `createDebugger`, and `deprecateHook`.
 *
 * An agent adds its own events by extending the map:
 * `interface MyEvents extends AgentKitEventMap { 'thing:done': { id: string } }`,
 * then `new EventBus<MyEvents>()`.
 */
export class EventBus {
    /** Underlying hookable instance — use for `addHooks`, `beforeEach`/`afterEach`, `createDebugger`, `deprecateHook`. */
    hooks = createHooks();
    onceWrappers = new Map();
    get ops() {
        return this.hooks;
    }
    /**
     * Registers a handler.
     *
     * @param event - Typed event name.
     * @param handler - Callback to register.
     * @returns A function that unsubscribes the handler.
     */
    on(event, handler) {
        return this.ops.hook(event, handler);
    }
    /**
     * Registers a handler that is removed after its first call.
     *
     * @param event - Typed event name.
     * @param handler - Callback to register.
     * @returns A function that unsubscribes the handler.
     */
    once(event, handler) {
        const original = handler;
        let unsubscribe;
        const wrapper = (...args) => {
            unsubscribe?.();
            this.forgetOnce(event, original, wrapper);
            return original(...args);
        };
        const handlers = this.onceWrappers.get(event) ?? new Map();
        const wrappers = handlers.get(original) ?? new Set();
        wrappers.add(wrapper);
        handlers.set(original, wrappers);
        this.onceWrappers.set(event, handlers);
        unsubscribe = this.ops.hook(event, wrapper);
        return () => {
            unsubscribe?.();
            this.forgetOnce(event, original, wrapper);
        };
    }
    /**
     * Removes a registered handler.
     *
     * @param event - Typed event name.
     * @param handler - Callback to remove.
     */
    off(event, handler) {
        const original = handler;
        const handlers = this.onceWrappers.get(event);
        const wrappers = handlers?.get(original);
        if (wrappers) {
            for (const wrapper of wrappers)
                this.ops.removeHook(event, wrapper);
            handlers?.delete(original);
            if (handlers?.size === 0)
                this.onceWrappers.delete(event);
        }
        this.ops.removeHook(event, original);
    }
    forgetOnce(event, handler, wrapper) {
        const handlers = this.onceWrappers.get(event);
        const wrappers = handlers?.get(handler);
        wrappers?.delete(wrapper);
        if (wrappers?.size === 0)
            handlers?.delete(handler);
        if (handlers?.size === 0)
            this.onceWrappers.delete(event);
    }
    /**
     * Emits a synchronous `before:*` event.
     *
     * Handlers may mutate the payload or throw to cancel. Async handlers are not
     * awaited; use {@link emitAsync} when a hook must block the operation.
     *
     * @param event - Typed event name.
     * @param payload - Mutable event payload.
     * @returns The same payload after handlers run.
     */
    emit(event, payload) {
        this.ops.callHookWith(syncCaller, event, [payload]);
        return payload;
    }
    /**
     * Sends a synchronous best-effort notification and swallows handler errors.
     *
     * @param event - Typed event name.
     * @param payload - Notification payload.
     */
    notify(event, payload) {
        this.ops.callHookWith(safeSyncCaller, event, [payload]);
    }
    /**
     * Emits an asynchronous `before:*` event.
     *
     * Handlers run in series; a handler that throws or rejects cancels the
     * operation.
     *
     * @param event - Typed event name.
     * @param payload - Mutable event payload.
     * @returns The same payload after handlers run.
     */
    async emitAsync(event, payload) {
        await this.ops.callHook(event, payload);
        return payload;
    }
    /**
     * Sends an asynchronous best-effort notification and swallows handler errors.
     *
     * @param event - Typed event name.
     * @param payload - Notification payload.
     */
    async notifyAsync(event, payload) {
        await this.ops.callHookWith(safeAsyncCaller, event, [payload]);
    }
}
//# sourceMappingURL=events.js.map